import { useRef } from 'react'
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect'

/**
 * Scroll-reveal / entrance hooks — CSS-first (IntersectionObserver + the Web
 * Animations API), no GSAP/ScrollTrigger. The public API is unchanged from the
 * old GSAP version so every section component and CMS site keeps working; only
 * the engine changed (compositor-driven transform/opacity, off the main thread).
 *
 * Durations/staggers stay in SECONDS (the old GSAP contract); we convert to ms.
 * Easing strings map: 'expo.out' -> a snappy expo cubic-bezier, 'none' ->
 * 'linear', anything else passes through. SSR/crawler-safe: the hooks only run
 * in the browser, so prerendered HTML ships visible; the initial hidden state is
 * applied on the client before paint (useIsomorphicLayoutEffect), and anything
 * already in view animates immediately so it never flashes hidden.
 */

const EXPO_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)'

function cssEasing(ease?: string): string {
  if (!ease || ease === 'expo.out') return EXPO_OUT
  if (ease === 'none') return 'linear'
  if (ease === 'power2.out' || ease === 'power3.out') return 'cubic-bezier(0.22, 1, 0.36, 1)'
  return ease
}

interface RevealStyle {
  y?: number
  scale?: number
  blur?: number
}

interface RunOptions {
  durationSec: number
  easing: string
  staggerSec: number
  delaySec: number
  /** grid stagger: GSAP `stagger.amount` spreads the total across all items */
  amountSec?: number
  /** run immediately on mount instead of on scroll-in (hero entrances) */
  immediate?: boolean
}

function hiddenCss({ y = 0, scale = 1, blur = 0 }: RevealStyle): Partial<CSSStyleDeclaration> {
  const transform = `translateY(${y}px)${scale !== 1 ? ` scale(${scale})` : ''}`
  return {
    opacity: '0',
    transform: transform === 'translateY(0px)' ? '' : transform,
    filter: blur ? `blur(${blur}px)` : '',
    willChange: 'opacity, transform',
  }
}

/**
 * Set the elements hidden (pre-paint), then animate each to its natural state
 * with the Web Animations API when the container scrolls into view (or right
 * away if `immediate`). Returns a cleanup that disconnects + cancels.
 *
 * Safety: opacity always ends at 1 with fill:'both', so a finished animation
 * can never leave content invisible.
 */
function animateInView(
  container: HTMLElement,
  elements: HTMLElement[],
  from: RevealStyle,
  opts: RunOptions
): () => void {
  if (!elements.length) return () => {}

  const hidden = hiddenCss(from)
  for (const el of elements) Object.assign(el.style, hidden)

  const total = elements.length
  const perStagger = opts.amountSec != null ? (total > 1 ? opts.amountSec / total : 0) : opts.staggerSec

  const fromKeyframe: Keyframe = {
    opacity: 0,
    transform: hidden.transform || 'translateY(0px)',
    ...(from.blur ? { filter: `blur(${from.blur}px)` } : {}),
  }
  const toKeyframe: Keyframe = {
    opacity: 1,
    transform: 'translateY(0px) scale(1)',
    ...(from.blur ? { filter: 'blur(0px)' } : {}),
  }

  const anims: Animation[] = []
  const run = () => {
    elements.forEach((el, i) => {
      // WAAPI overrides the inline hidden style during play and (fill:'both')
      // holds the final visible state afterwards.
      const anim = el.animate([fromKeyframe, toKeyframe], {
        duration: opts.durationSec * 1000,
        easing: opts.easing,
        delay: (opts.delaySec + perStagger * i) * 1000,
        fill: 'both',
      })
      anims.push(anim)
    })
  }

  if (opts.immediate) {
    run()
    return () => anims.forEach((a) => a.cancel())
  }

  let io: IntersectionObserver | null = null
  const start = () => {
    io?.disconnect()
    io = null
    run()
  }
  // Already in view on mount? animate now (avoids a frame's flash of hidden).
  if (container.getBoundingClientRect().top < (window.innerHeight || 0) * 0.98) {
    start()
  } else {
    io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) start()
      },
      { rootMargin: '0px 0px -2% 0px' }
    )
    io.observe(container)
  }

  return () => {
    io?.disconnect()
    anims.forEach((a) => a.cancel())
  }
}

function prefersReducedMotion(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches
}

interface ScrollRevealOptions {
  duration?: number
  y?: number
  stagger?: number
  /** Accepted for API compatibility (old ScrollTrigger start); no longer used. */
  start?: string
  ease?: string
  delay?: number
}

interface LuxeRevealOptions extends ScrollRevealOptions {
  blur?: number
  scale?: number
}

const LUXE_DURATION = 0.5

export function useScrollReveal<T extends HTMLElement>(
  selector: string = '.reveal',
  options: ScrollRevealOptions = {}
) {
  const containerRef = useRef<T>(null)
  const { duration = 0.5, y = 24, stagger = 0.06, ease = 'expo.out', delay = 0 } = options

  useIsomorphicLayoutEffect(() => {
    if (prefersReducedMotion() || !containerRef.current) return
    const els = Array.from(containerRef.current.querySelectorAll<HTMLElement>(selector))
    return animateInView(
      containerRef.current,
      els,
      { y },
      {
        durationSec: duration,
        easing: cssEasing(ease),
        staggerSec: stagger,
        delaySec: delay,
      }
    )
  }, [selector, duration, y, stagger, ease, delay])

  return containerRef
}

export function useLuxeReveal<T extends HTMLElement>(selector: string = '.reveal', options: LuxeRevealOptions = {}) {
  const containerRef = useRef<T>(null)
  const {
    duration = LUXE_DURATION,
    y = 30,
    blur = 4,
    scale = 0.98,
    stagger = 0.08,
    ease = 'expo.out',
    delay = 0,
  } = options

  useIsomorphicLayoutEffect(() => {
    if (prefersReducedMotion() || !containerRef.current) return
    const els = Array.from(containerRef.current.querySelectorAll<HTMLElement>(selector))
    return animateInView(
      containerRef.current,
      els,
      { y, scale, blur },
      {
        durationSec: duration,
        easing: cssEasing(ease),
        staggerSec: stagger,
        delaySec: delay,
      }
    )
  }, [selector, duration, y, blur, scale, stagger, ease, delay])

  return containerRef
}

export function useLuxeGrid<T extends HTMLElement>(selector: string = '.card', options: LuxeRevealOptions = {}) {
  const containerRef = useRef<T>(null)
  const { duration = LUXE_DURATION, y = 20, blur = 3, scale = 0.98, stagger = 0.05, ease = 'expo.out' } = options

  useIsomorphicLayoutEffect(() => {
    if (prefersReducedMotion() || !containerRef.current) return
    const els = Array.from(containerRef.current.querySelectorAll<HTMLElement>(selector))
    return animateInView(
      containerRef.current,
      els,
      { y, scale, blur },
      {
        durationSec: duration,
        easing: cssEasing(ease),
        staggerSec: stagger,
        delaySec: 0,
        amountSec: stagger * els.length,
      }
    )
  }, [selector, duration, y, blur, scale, stagger, ease])

  return containerRef
}

export function useStaggeredGrid<T extends HTMLElement>(selector: string = '.card', options: ScrollRevealOptions = {}) {
  const containerRef = useRef<T>(null)
  const { duration = 0.45, y = 16, stagger = 0.04, ease = 'expo.out' } = options

  useIsomorphicLayoutEffect(() => {
    if (prefersReducedMotion() || !containerRef.current) return
    const els = Array.from(containerRef.current.querySelectorAll<HTMLElement>(selector))
    return animateInView(
      containerRef.current,
      els,
      { y },
      {
        durationSec: duration,
        easing: cssEasing(ease),
        staggerSec: stagger,
        delaySec: 0,
        amountSec: stagger * els.length,
      }
    )
  }, [selector, duration, y, stagger, ease])

  return containerRef
}

/**
 * Parallax: CSS scroll-driven where supported (ScrollTimeline), else a no-op
 * (the element sits static — graceful, and no main-thread scroll work).
 */
export function useParallax<T extends HTMLElement>(speed: number = 0.3) {
  const elementRef = useRef<T>(null)

  useIsomorphicLayoutEffect(() => {
    const el = elementRef.current
    if (prefersReducedMotion() || !el) return
    // ScrollTimeline is a non-standard global whose presence/type differs across
    // tsconfig DOM libs; read it via Reflect.get (any) so this compiles the same
    // everywhere, then feature-detect at runtime.
    const scrollTimelineKey: string = 'ScrollTimeline'
    const ScrollTimelineCtor = Reflect.get(window, scrollTimelineKey) as
      | (new (options: object) => AnimationTimeline)
      | undefined
    if (typeof el.animate !== 'function' || typeof ScrollTimelineCtor !== 'function') return
    const parent = el.parentElement
    if (!parent) return
    let anim: Animation | null = null
    try {
      anim = el.animate([{ transform: 'translateY(0)' }, { transform: `translateY(${-30 * speed}%)` }], {
        fill: 'both',
        timeline: new ScrollTimelineCtor({ source: parent }),
      } as KeyframeAnimationOptions)
    } catch {
      anim = null
    }
    return () => anim?.cancel()
  }, [speed])

  return elementRef
}

export function useHeroReveal<T extends HTMLElement>(delay: number = 0.1) {
  const containerRef = useRef<T>(null)

  useIsomorphicLayoutEffect(() => {
    if (prefersReducedMotion() || !containerRef.current) return
    const els = Array.from(containerRef.current.children) as HTMLElement[]
    return animateInView(
      containerRef.current,
      els,
      { y: 16 },
      {
        durationSec: 0.5,
        easing: EXPO_OUT,
        staggerSec: 0.08,
        delaySec: delay,
        immediate: true,
      }
    )
  }, [delay])

  return containerRef
}

export function useLuxeHero<T extends HTMLElement>(delay: number = 0.1) {
  const containerRef = useRef<T>(null)

  useIsomorphicLayoutEffect(() => {
    if (prefersReducedMotion() || !containerRef.current) return
    const els = Array.from(containerRef.current.children) as HTMLElement[]
    return animateInView(
      containerRef.current,
      els,
      { y: 24, scale: 0.98, blur: 4 },
      {
        durationSec: 0.6,
        easing: EXPO_OUT,
        staggerSec: 0.1,
        delaySec: delay,
        immediate: true,
      }
    )
  }, [delay])

  return containerRef
}
